<template>
  <div class="landing-page">
    <!-- Hero -->
    <section class="hero">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 col-md-7">
            <p class="eyebrow hero-rise">The Venue Engine</p>
            <h1 class="hero-title hero-rise hero-rise-1">
              Run your whole <span class="accent-word">venue</span><br class="d-none d-md-inline" /> from one <span class="accent-word">engine</span>.
            </h1>
            <p class="hero-sub hero-rise hero-rise-2">
              Bookings, payments, waivers, photos, surveys, marketing, and on-site
              operations. Every customer touchpoint connected on a single engine, built
              for booked experience venues.
            </p>
            <div class="hero-cta hero-rise hero-rise-3">
              <a href="/contact" class="btn-cta-primary">Get a demo</a>
              <a href="/pricing" class="btn-cta-secondary">See pricing &rarr;</a>
            </div>
            <p class="hero-microcopy hero-rise hero-rise-4">
              <svg-icon name="check" :size="14" />
              Every feature included &middot; $50/mo minimum &middot; cancel anytime
            </p>
            <div class="hero-proof hero-rise hero-rise-5">
              <div class="hero-proof-avatars">
                <img
                  v-for="t in testimonialsWithLogos"
                  :key="t.venue"
                  :src="t.avatar"
                  :alt="t.venue"
                  width="28"
                  height="28"
                />
              </div>
              <span>Trusted by 1,000+ locations across all venue and event types</span>
            </div>
          </div>
          <div class="col-lg-6 col-md-5 hero-image-col">
            <div class="hero-visual">
              <div class="hero-shot-main hero-float">
                <span class="hero-shot-bar" aria-hidden="true"><i></i><i></i><i></i></span>
                <img
                  src="/img/shots/hero-dashboard.png"
                  alt="An Off The Couch dashboard with live revenue and booking widgets"
                  loading="eager"
                  fetchpriority="high"
                  width="3159"
                  height="1988"
                />
              </div>
              <div class="hero-tablet" aria-hidden="true">
                <img
                  src="/img/shots/hero-kanban.png"
                  alt=""
                  loading="eager"
                  width="1160"
                  height="722"
                />
              </div>
              <div class="hero-phone" aria-hidden="true">
                <span class="hero-phone-speaker"></span>
                <img src="/img/shots/hero-mobile.png" alt="" loading="eager" width="288" height="610" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Proof band: numbers + verticals on one surface -->
    <section class="proof-band section-band">
      <div class="container">
        <div class="trust-grid">
          <div class="trust-item">
            <strong>1</strong>
            <span>Engine, every venue</span>
          </div>
          <div class="trust-item">
            <strong>All</strong>
            <span>Modules included</span>
          </div>
          <div class="trust-item">
            <strong>$50</strong>
            <span>Monthly minimum</span>
          </div>
          <div class="trust-item">
            <strong>$0</strong>
            <span>Per-user or location fees</span>
          </div>
        </div>
        <div class="verticals-strip">
          <p class="verticals-eyebrow">Built for</p>
          <p class="verticals-list">
            Escape rooms <span class="dot">&middot;</span>
            Axe throwing <span class="dot">&middot;</span>
            VR centers <span class="dot">&middot;</span>
            Indoor mini golf <span class="dot">&middot;</span>
            Challenge arcades <span class="dot">&middot;</span>
            Haunted houses <span class="dot">&middot;</span>
            Adventure tours <span class="dot">&middot;</span>
            Any booked experience venue
          </p>
        </div>
      </div>
    </section>

    <!-- Customer journey: how the modules synergize -->
    <journey-flow />

    <!-- Why one platform -->
    <section class="value-section">
      <div class="container">
        <div class="row justify-content-center text-center mb-5">
          <div class="col-lg-8">
            <h2 class="section-title">Why venues run on the engine</h2>
            <p class="section-sub">
              No integrations to wire, no data stuck in silos, no per-seat math.
              Three reasons operators switch and stay.
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4 mb-4" v-for="value in values" :key="value.title">
            <div class="value-card">
              <div class="value-icon">
                <svg-icon :name="value.icon" :size="22" />
              </div>
              <h3>{{ value.title }}</h3>
              <p>{{ value.body }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Feature tabs -->
    <section class="features-overview section-band">
      <div class="container">
        <div class="row justify-content-center text-center mb-5">
          <div class="col-lg-8">
            <h2 class="section-title">What runs the engine</h2>
            <p class="section-sub">
              From the moment a customer books to the photo they share after, every step
              of the experience runs on the same connected modules.
            </p>
          </div>
        </div>

        <div class="feature-tabs">
          <div class="feature-tab-list" role="tablist">
            <button
              v-for="(tab, idx) in featureTabs"
              :key="tab.key"
              :class="['feature-tab', { active: activeTab === idx }]"
              @click="activeTab = idx"
              :aria-selected="activeTab === idx"
              role="tab"
            >
              <svg-icon :name="tab.icon" :size="16" />
              <span>{{ tab.label }}</span>
            </button>
          </div>

          <div class="feature-tab-panel">
            <div class="row align-items-center">
              <div class="col-md-6">
                <h3 class="feature-tab-title">{{ featureTabs[activeTab].title }}</h3>
                <p class="feature-tab-body">{{ featureTabs[activeTab].body }}</p>
                <ul class="feature-tab-list-points">
                  <li v-for="point in featureTabs[activeTab].points" :key="point">
                    <svg-icon name="check" :size="14" /> {{ point }}
                  </li>
                </ul>
                <a href="/features" class="feature-tab-link">
                  Explore all features <svg-icon name="arrow-right" :size="14" />
                </a>
              </div>
              <div class="col-md-6">
                <img
                  :src="featureTabs[activeTab].img"
                  :alt="featureTabs[activeTab].label + ' screenshot'"
                  class="feature-tab-image"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials: one spotlighted story, logo strip as navigation -->
    <section class="testimonials">
      <div class="testimonial-slab section-band section-band--tilt">
        <div class="container">
          <div
            class="row align-items-center testimonial-featured"
            @mouseenter="pauseRefAuto"
            @mouseleave="resumeRefAuto"
          >
            <div class="col-md-4 mb-4 mb-md-0">
              <div class="testimonial-media">
                <img
                  v-if="activeRef.avatar"
                  :src="activeRef.avatar"
                  :alt="activeRef.venue + ' logo'"
                  loading="lazy"
                  width="170"
                  height="170"
                />
              </div>
            </div>
            <div class="col-md-8">
              <transition name="ref-fade" mode="out-in">
                <div :key="refIndex">
                  <p class="testimonial-eyebrow">Operator story</p>
                  <blockquote class="testimonial-pull">
                    &ldquo;{{ activeRef.pull }}&rdquo;
                  </blockquote>
                  <p class="testimonial-attrib">
                    <strong>{{ activeRef.name }}</strong>
                    <span>{{ activeRef.venue }}</span>
                  </p>
                  <a
                    v-if="activeRef.url"
                    :href="activeRef.url"
                    target="_blank"
                    rel="noopener"
                    class="testimonial-link"
                  >
                    Visit {{ activeRef.venue }} <svg-icon name="arrow-up-right" :size="14" />
                  </a>
                </div>
              </transition>
            </div>
          </div>

          <div class="testimonial-selector" role="tablist" aria-label="Customer stories">
            <button
              v-for="(t, i) in testimonials"
              :key="t.venue"
              :class="['selector-item', { active: i === refIndex }]"
              role="tab"
              :aria-selected="i === refIndex"
              @click="selectRef(i)"
            >
              <span class="selector-track">
                <span
                  v-if="i === refIndex"
                  :key="'fill-' + refIndex"
                  :class="['selector-fill', { animating: refAutoRunning }]"
                ></span>
              </span>
              <span class="selector-body">
                <img v-if="t.avatar" :src="t.avatar" :alt="''" loading="lazy" width="26" height="26" />
                <span class="selector-name">{{ t.venue }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing teaser -->
    <section class="pricing-teaser">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="pricing-teaser-card">
              <div class="row align-items-center">
                <div class="col-md-7">
                  <p class="eyebrow">Simple pricing</p>
                  <h2 class="pricing-teaser-title">
                    Every feature, always on, $50 a month minimum. Pay for what you use.
                  </h2>
                  <p class="pricing-teaser-sub">
                    No tiers, no per-user fees, no per-location fees. Usage is itemized in
                    cents per action; when it adds up to more than $50, you pay the usage.
                    Your invoice reads like a receipt of what your venue actually did.
                  </p>
                </div>
                <div class="col-md-5 text-md-right text-center">
                  <a href="/pricing" class="btn-cta-primary">See full pricing</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Demo CTA + form -->
    <section id="contactus" class="demo-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-6 mb-4 mb-md-0">
            <h2 class="section-title">See it live</h2>
            <p class="section-sub">
              Live demos at a time that works for you. We'll walk through the platform
              with your venue's specific workflow in mind and answer any questions along the way.
            </p>
            <div class="demo-info">
              <div class="demo-info-item">
                <svg-icon name="mail" :size="20" />
                <span>
                  Email
                  <a href="mailto:contact@offthecouch.io">contact@offthecouch.io</a>
                </span>
              </div>
              <div class="demo-info-item">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M13.545 2.907a13.2 13.2 0 0 0-3.257-1.011.05.05 0 0 0-.052.025c-.141.25-.297.577-.406.833a12.2 12.2 0 0 0-3.658 0 8 8 0 0 0-.412-.833.05.05 0 0 0-.052-.025c-1.125.194-2.22.534-3.257 1.011a.04.04 0 0 0-.021.018C.356 6.024-.213 9.047.066 12.032q.003.022.021.037a13.3 13.3 0 0 0 3.995 2.02.05.05 0 0 0 .056-.019q.463-.63.818-1.329a.05.05 0 0 0-.01-.059l-.018-.011a9 9 0 0 1-1.248-.595.05.05 0 0 1-.02-.066l.015-.019q.127-.095.248-.195a.05.05 0 0 1 .051-.007c2.619 1.196 5.454 1.196 8.041 0a.05.05 0 0 1 .053.007q.121.1.248.195a.05.05 0 0 1-.004.085 8 8 0 0 1-1.249.594.05.05 0 0 0-.03.03.05.05 0 0 0 .003.041c.24.465.515.909.817 1.329a.05.05 0 0 0 .056.019 13.2 13.2 0 0 0 4.001-2.02.05.05 0 0 0 .021-.037c.334-3.451-.559-6.449-2.366-9.106a.03.03 0 0 0-.02-.019m-8.198 7.307c-.789 0-1.438-.724-1.438-1.612s.637-1.613 1.438-1.613c.807 0 1.45.73 1.438 1.613 0 .888-.637 1.612-1.438 1.612m5.316 0c-.788 0-1.438-.724-1.438-1.612s.637-1.613 1.438-1.613c.807 0 1.451.73 1.438 1.613 0 .888-.631 1.612-1.438 1.612"/>
                </svg>
                <span>
                  Or join our
                  <a href="https://discord.gg/752E9HM6tt" target="_blank" rel="noopener">Discord community</a>
                </span>
              </div>
            </div>
          </div>
          <div class="col-md-5 ml-md-auto">
            <div class="demo-form">
              <h3>Tell us about your venue</h3>
              <form @submit.prevent="submitDemoRequestForm" novalidate>
                <div class="row">
                  <div class="col-sm-6">
                    <label class="form-label" for="lp-first-name">First name<span class="form-required-mark">*</span></label>
                    <base-input
                      v-model="user.first_name"
                      id="lp-first-name"
                      placeholder="First name"
                      autocomplete="given-name"
                      :aria-invalid="fieldErrors.first_name ? 'true' : null"
                      :aria-describedby="fieldErrors.first_name ? 'lp-first-name-error' : null"
                    />
                    <span v-if="fieldErrors.first_name" id="lp-first-name-error" class="form-field-error">Required</span>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label" for="lp-last-name">Last name</label>
                    <base-input v-model="user.last_name" id="lp-last-name" placeholder="Last name" autocomplete="family-name" />
                  </div>
                </div>
                <label class="form-label" for="lp-email">Email<span class="form-required-mark">*</span></label>
                <base-input
                  v-model="user.email"
                  id="lp-email"
                  placeholder="you@venue.com"
                  type="email"
                  autocomplete="email"
                  :aria-invalid="fieldErrors.email ? 'true' : null"
                  :aria-describedby="fieldErrors.email ? 'lp-email-error' : null"
                />
                <span v-if="fieldErrors.email" id="lp-email-error" class="form-field-error">{{ fieldErrors.email }}</span>

                <label class="form-label" for="lp-phone">Phone</label>
                <base-input v-model="user.phone" id="lp-phone" placeholder="Optional" type="tel" autocomplete="tel" />

                <label class="form-label" for="lp-venue">Venue name</label>
                <base-input v-model="user.company_name" id="lp-venue" placeholder="Venue name" autocomplete="organization" />

                <div class="text-right">
                  <button type="submit" class="btn-cta-primary" :disabled="submitting">
                    {{ submitting ? 'Sending' : 'Request a demo' }}
                  </button>
                </div>
                <p v-if="submitState === 'success'" class="form-status form-status-success" ref="successMsg">
                  <img src="/img/pandas/celebrate.png" alt="" class="form-status-panda" />
                  Thanks. We'll reach out within one business day.
                </p>
                <p v-if="submitState === 'error'" class="form-status form-status-error">
                  Something went wrong sending that. Email us at contact@offthecouch.io and we'll sort it.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import JourneyFlow from '@/components/JourneyFlow.vue';

export default {
  name: "Landing",
  components: { JourneyFlow },
  data() {
    return {
      user: { first_name: '', last_name: '', email: '', company_name: '', phone: '' },
      fieldErrors: {},
      submitting: false,
      submitState: null,
      activeTab: 0,
      values: [
        {
          icon: 'layers',
          title: 'One engine, not a stack',
          body: "Bookings, waivers, photos, payments, surveys, and operations share a single data model. The customer record follows them through every interaction with your venue.",
        },
        {
          icon: 'dollar-sign',
          title: 'Honest pricing',
          body: "One plan. Every feature. $50/mo minimum, then pay for what you use. No per-seat fees. No per-location fees. The whole engine is unlocked from day one.",
        },
        {
          icon: 'headphones',
          title: 'Made by operators',
          body: "We use Off The Couch to run our own venues. When you have a question or a feature request, you reach the people who actually build the engine.",
        },
      ],
      featureTabs: [
        {
          key: 'bookings', label: 'Bookings', icon: 'calendar',
          title: 'Powerful booking and resource management',
          body: "Give customers more times to choose from without worrying about conflicts. OTC automatically manages availability around your rooms, staff, equipment, and other resources, blocking and opening booking times as availability changes. The result is more bookable inventory, more revenue opportunities, and no double bookings.",
          points: [
            'Automatic resource-based blocking and availability',
            'Maximize bookable times without double booking',
            'Manage rooms, staff, equipment, and other resources',
            'Four views: list, calendar, resource calendar, and kanban',
          ],
          img: '/img/shots/landing-bookings.png',
        },
        {
          key: 'site', label: 'Booking site', icon: 'layout',
          title: 'A branded booking site, on your domain',
          body: "Run multiple booking templates from one account, each with its own URL, branding, event list, and checkout rules. Custom fields built once and reused across every template.",
          points: [
            'Multiple templates with unique URLs and styling',
            'Brand-matched colors, fonts, and copy',
            'Reusable custom-field library across templates',
            'Embeddable on your existing website',
          ],
          img: '/img/shots/landing-booking-site.png',
        },
        {
          key: 'payments', label: 'Payments', icon: 'shopping-cart',
          title: 'Gateways, gift cards, promo codes, inventory',
          body: "A complete payment stack. Multiple gateway integrations, digital and physical gift cards with custom values, promo codes with eligibility rules, inventory of merchandise and add-ons, customer credit, and saved cards on file.",
          points: [
            'Major gateways supported, with custom payment methods',
            'Gift cards with overlays, expiration, and custom values',
            'Promo codes with eligibility rules and code groups',
            'Customer credit and saved-card deposits',
          ],
          img: '/img/shots/landing-payments.png',
        },
        {
          key: 'events', label: 'Event tracking', icon: 'gamepad',
          title: 'Live capture and an in-house hint system',
          body: "Hosts log start times, completion, hints, and notes during every session. The data feeds analytics, leaderboards, and customer recaps. A built-in web hint system delivers text, audio, and video cues to the in-room guest screen.",
          points: [
            'Web-based hint system with text, audio, and video',
            'Story videos and music tied to session state',
            'Per-host and per-event performance analytics',
            'Team leaderboards with venue-screen fullscreen mode',
          ],
          img: '/img/shots/landing-event-tracking.png',
        },
        {
          key: 'photos', label: 'Photos', icon: 'camera',
          title: 'High-quality photos in one click',
          body: "Capture full-resolution photos, apply branded overlays automatically with dynamic event-data variables, and send the entire batch to every guest in the group with a single click.",
          points: [
            'Uncompressed CDN storage',
            'Overlay templates with dynamic event data',
            'One-click distribution to every participant',
            'Linked to booking, customer, and waiver record',
          ],
          img: '/img/shots/landing-photos.png',
        },
        {
          key: 'mail', label: 'Marketing & email', icon: 'mail',
          title: 'Powerful email automation, built around your operation',
          body: "Create unlimited, highly customizable email templates for every part of the customer journey and your internal operations. Build them visually with a drag-and-drop editor, then use multi-step automations to control exactly what gets sent, when it gets sent, and who receives it.",
          points: [
            'Unlimited custom email templates',
            'Drag-and-drop visual template builder',
            'Multi-step automations with triggers, waits, and conditions',
            'Personalize by event, group size, and booking data',
          ],
          img: '/img/shots/landing-email.png',
        },
        {
          key: 'reports', label: 'Reports & dashboards', icon: 'bar-chart',
          title: 'Custom reports, live dashboards, leaderboards',
          body: "Build custom reports with the fields you care about. Save them, run them on demand for any time range, with no caps. Live dashboards with chart, statistic, and activity widgets you can pin to a venue screen.",
          points: [
            'Custom report builder with saved configurations',
            'Unlimited reports across any time period',
            'Live dashboards with chart, stat, and activity widgets',
            'Team leaderboards in fullscreen for venue displays',
          ],
          img: '/img/shots/landing-reports.png',
        },
      ],
      refIndex: 0,
      refTimer: null,
      refInteracted: false,
      testimonials: [
        {
          name: 'Jonathan, Owner', venue: 'Escaparium', url: 'https://www.escaparium.ca/', avatar: '/img/escaparium.jpg',
          pull: "Off The Couch has become an essential tool for running our business. They genuinely listen, take our feedback seriously, and keep evolving the system around the real-world needs of operators.",
        },
        {
          name: 'Michaela', venue: 'The Great Escape Room', url: 'https://thegreatescaperoom.com/', avatar: '/img/great-escape-room.jpg',
          pull: "After using multiple booking platforms, OTC is easily the most user-friendly and reliable system we have experienced. For a multi-location operation like ours, we could not ask for better.",
        },
        {
          name: 'Sarah, Owner', venue: 'Omescape', url: 'https://omescape.us/', avatar: '/img/omescape.jpg',
          pull: "The payment options made it easy to move from full payment upfront to a deposit model, and resource management helped us open up more same-day availability while keeping labor costs under control.",
        },
        {
          name: 'Curtis, Owner', venue: 'Escape Ventures', url: 'https://theescapeventures.com/', avatar: '/img/escape-ventures.jpg',
          pull: "We made the switch to Off The Couch years ago and have not looked back. Now with six locations along the East Coast, I could not think of a better platform to manage every one of them.",
        },
        {
          name: 'Nick, Owner', venue: 'Redacted', url: 'https://www.redactedescape.com/', avatar: '/img/redacted.jpg',
          pull: "Off The Couch let us condense the number of applications we use. They make changes based on feedback from the community, and migrating from our old booking platform was easy.",
        },
        {
          name: 'Patrick, Owner', venue: 'Appleseed Avenue', url: 'https://appleseedavenue.com', avatar: '/img/appleseed-avenue.jpg',
          pull: "This is a no-brainer. Pay only for what you need, and know it's top quality. There is no other choice in booking software.",
        },
        {
          name: 'Jennifer S Broncos', venue: 'Tactical Escape 101', url: 'https://tacticalescape101.com/', avatar: '/img/tactical101.jpg',
          pull: "Off The Couch is what we've been waiting for for ten years. A system made by escape room owners FOR escape room owners.",
        },
        {
          name: 'Jake', venue: 'Puzzleworks', url: 'https://www.puzzle.works/', avatar: '/img/puzzleworks.jpg',
          pull: "We save money and time having everything in one place, and the customer service response time is unprecedented.",
        },
        {
          name: 'Dino', venue: 'Hint Hunt South Africa', url: 'https://hinthunt.co.za/', avatar: '/img/hinthunt.jpg',
          pull: "Finally found a booking system that cares about data as much as we do! Slick, feature rich, intuitive, and affordable.",
        },
        {
          name: 'Gavin Dean, Director', venue: 'Great Yarmouth Escape Rooms', url: 'https://www.greatyarmouthescaperooms.co.uk/', avatar: '/img/gyer.jpg',
          pull: "We joined as beta users in 2023. Since then it has become an integral part of the business.",
        },
        {
          name: 'Jamie', venue: 'Breakpoint Escape Rooms', url: 'https://www.pointescaperooms.com/', avatar: '/img/breakpoint.jpg',
          pull: "We replaced multiple services with one package, and got better customer service in addition. I won't be switching again!",
        },
      ],
    };
  },
  computed: {
    activeRef() {
      return this.testimonials[this.refIndex];
    },
    // The hero avatar row only shows real logos; monograms would read as noise
    // at 28px, so venues awaiting a logo file are skipped there.
    testimonialsWithLogos() {
      return this.testimonials.filter(t => t.avatar);
    },
    refAutoRunning() {
      return this.refTimer !== null;
    },
  },
  mounted() {
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reducedMotion) this.startRefAuto();
  },
  beforeDestroy() {
    this.stopRefAuto();
  },
  methods: {
    selectRef(i) {
      this.refIndex = i;
      this.refInteracted = true;
      this.stopRefAuto();
    },
    startRefAuto() {
      if (this.refInteracted || this.refTimer) return;
      this.refTimer = setInterval(() => {
        this.refIndex = (this.refIndex + 1) % this.testimonials.length;
      }, 8000);
    },
    stopRefAuto() {
      if (this.refTimer) {
        clearInterval(this.refTimer);
        this.refTimer = null;
      }
    },
    pauseRefAuto() {
      this.stopRefAuto();
    },
    resumeRefAuto() {
      if (!this.refInteracted) this.startRefAuto();
    },
    validateForm() {
      const errs = {};
      if (!this.user.first_name.trim()) errs.first_name = true;
      const email = this.user.email.trim();
      if (!email) errs.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email';
      this.fieldErrors = errs;
      return Object.keys(errs).length === 0;
    },
    async submitDemoRequestForm() {
      if (!this.validateForm()) return;
      this.submitting = true;
      this.submitState = null;
      try {
        await axios.post('https://api.offthecouch.io/demo-request', { user: this.user });
        this.submitState = 'success';
        this.user = { first_name: '', last_name: '', email: '', company_name: '', phone: '' };
        this.fieldErrors = {};
        this.$nextTick(() => {
          if (this.$refs.successMsg) {
            this.$refs.successMsg.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      } catch (err) {
        this.submitState = 'error';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.landing-page { padding-top: 72px; }

/* Hero */

// The hero owns the first viewport deliberately: sized to the fold so the
// proof band starts exactly at the break, never sliced mid-number.
.hero {
  display: flex;
  align-items: center;
  min-height: min(680px, calc(100vh - 72px));
  padding: 2rem 0 2.5rem;
  position: relative;
  overflow: hidden;

  > .container { width: 100%; }

  &::before {
    content: '';
    position: absolute;
    top: -20%; right: -12%;
    width: 720px; height: 720px;
    background: radial-gradient(circle, rgba(225, 78, 202, 0.2), transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
}

.hero-sub {
  font-size: 1.15rem;
  color: $muted;
  line-height: 1.55;
  margin-bottom: 1.6rem;
}

.hero-cta {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.hero-microcopy {
  color: $muted-2;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  ::v-deep .svg-icon { color: $teal; }
}

/* The one gradient moment: the brand word, pink into teal like the logo */
.accent-word {
  background: linear-gradient(92deg, $primary 15%, $teal 85%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Entrance stagger: content rises once on load */
.hero-rise {
  animation: hero-rise 0.55s cubic-bezier(0.2, 0.6, 0.25, 1) both;
}
.hero-rise-1 { animation-delay: 0.07s; }
.hero-rise-2 { animation-delay: 0.14s; }
.hero-rise-3 { animation-delay: 0.21s; }
.hero-rise-4 { animation-delay: 0.28s; }
.hero-rise-5 { animation-delay: 0.34s; }

@keyframes hero-rise {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Above-the-fold proof: real venues, real logos */
.hero-proof {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.25rem;

  span {
    color: $muted-2;
    font-size: 0.82rem;
    line-height: 1.4;
  }
}

.hero-proof-avatars {
  display: flex;
  flex-shrink: 0;

  img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #1d1e3a;

    & + img { margin-left: -9px; }
  }
}

/* Floating product moments over the illustration */
.hero-visual {
  position: relative;
  isolation: isolate;
  // Breathing room for the side shots that overlap the main frame's corners
  padding: 2.5rem 0 3rem;

  // Soft two-tone glow so the composition sits in light instead of flat dark
  &::before {
    content: '';
    position: absolute;
    inset: -14%;
    z-index: -1;
    background: radial-gradient(
      closest-side,
      rgba(225, 78, 202, 0.13),
      rgba(46, 201, 183, 0.07) 55%,
      transparent 78%
    );
    pointer-events: none;
  }
}

/* The functionality sampling: calendar in a browser frame, reports and
   photos peeking from the corners */
.hero-shot-main {
  position: relative;
  margin-left: 5%;
  margin-right: -5%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: #1c1c29;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.5);
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
}

.hero-shot-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;

    &:nth-child(1) { background: rgba(255, 95, 87, 0.75); }
    &:nth-child(2) { background: rgba(254, 188, 46, 0.75); }
    &:nth-child(3) { background: rgba(40, 200, 64, 0.75); }
  }
}

// Tablet shell: the workflow board overlaps the dashboard's bottom-right
// Mid layer: workflow board, counter-tilted so its edges break from the frame.
// The upright dashboard anchors; the two accent devices diverge 9 degrees so
// their bezels never form a parallel-edge blob against the navy background
// The two devices dock on opposite bottom corners of the browser frame,
// hanging below its edge: two symmetric feet, one stable triangle
.hero-tablet {
  position: absolute;
  width: 56%;
  bottom: -4%;
  left: -5%;
  z-index: 2;
  padding: 10px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: #0e0e1a;
  box-shadow: 0 24px 55px rgba(0, 0, 0, 0.55);
  animation: hero-float 6s ease-in-out infinite alternate;
  animation-delay: -3s;

  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

// Phone shell for the mobile capture: dark bezel, speaker pill, rounded screen
.hero-phone {
  position: absolute;
  width: 23%;
  top: -6%;
  right: -8%;
  z-index: 3;
  padding: 7px 6px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: #0e0e1a;
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.6);
  animation: hero-float 6s ease-in-out infinite alternate;
  animation-delay: -4.5s;

  .hero-phone-speaker {
    display: block;
    width: 30%;
    height: 4px;
    margin: 2px auto 6px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 16px;
  }
}

.hero-float {
  animation: hero-float 7s ease-in-out infinite alternate;
}

@keyframes hero-float {
  from { transform: translateY(0); }
  to   { transform: translateY(-10px); }
}

@media (prefers-reduced-motion: reduce) {
  .hero-rise { animation: none; }
  .hero-float,
  .hero-tablet,
  .hero-phone { animation: none; }
}

/* Verticals strip (inside the proof band) */

.verticals-strip {
  padding: 2.25rem 0 0;
  margin-top: 2.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  text-align: center;
}

.verticals-eyebrow {
  color: $teal;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  margin-bottom: 0.6rem;
}

.verticals-list {
  color: $muted;
  font-size: 0.92rem;
  line-height: 1.8;
  margin: 0;
  max-width: 920px;
  margin-left: auto;
  margin-right: auto;
  .dot {
    color: $teal;
    margin: 0 0.4rem;
    opacity: 0.7;
  }
}

.hero-image-col { position: relative; z-index: 1; }

// Air between the copy column and the screenshot composition on desktop
@media (min-width: 992px) {
  .hero-image-col { padding-left: 3.5rem; }
}

/* Proof band: stats + verticals on one shared surface */

.proof-band {
  margin-top: 0;
  padding: 2.5rem 0;
}

.trust-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  text-align: center;
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); gap: 2rem; }
}

.trust-item {
  strong {
    display: block;
    font-size: 1.9rem;
    color: white;
    font-weight: 800;
    margin-bottom: 0.25rem;
  }
  span {
    color: $muted-2;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
}

/* Value cards */

.value-section { padding: 3.5rem 0; }

.value-card {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 2rem;
  height: 100%;
  transition: border-color 0.2s ease, transform 0.2s ease;
  &:hover { border-color: $primary-border; transform: translateY(-2px); }
  h3 { color: white; font-size: 1.2rem; font-weight: 600; margin: 1rem 0 0.5rem; }
  p { color: $muted; font-size: 0.95rem; line-height: 1.55; margin: 0; }
}

.value-icon {
  width: 48px; height: 48px;
  border-radius: 10px;
  background: $teal-soft;
  border: 1px solid rgba(46, 201, 183, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  ::v-deep .svg-icon { color: $teal; }
}

/* Feature tabs */

.features-overview { padding: 4rem 0 4.5rem; }

.feature-tabs {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  overflow: hidden;
}

.feature-tab-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.15);
}

.feature-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  color: $muted-2;
  padding: 0.5rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover { color: white; background: rgba(255, 255, 255, 0.04); }
  &.active {
    color: $primary;
    background: $primary-soft;
    border: 1px solid $primary-border;
    padding: calc(0.5rem - 1px) calc(0.85rem - 1px);
  }
}

.feature-tab-panel { padding: 2.5rem; }

.feature-tab-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.feature-tab-body {
  color: $muted;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
}

.feature-tab-list-points {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  li {
    color: $muted;
    padding: 0.4rem 0;
    font-size: 0.92rem;
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    ::v-deep .svg-icon { color: $teal; margin-top: 0.2rem; flex-shrink: 0; }
  }
}

.feature-tab-link {
  color: $primary;
  font-weight: 600;
  text-decoration: none;
  &:hover { text-decoration: none; opacity: 0.8; }
}

.feature-tab-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
}

/* Testimonials: one spotlighted story on a tilted slab, logo strip as navigation */

.testimonials {
  padding: 2.5rem 0;
}

// Tilt surface comes from the shared section-band--tilt token
.testimonial-slab {
  padding: 4.5rem 0 3.5rem;
}

.testimonial-featured {
  max-width: 1040px;
  margin: 0 auto 3rem;
}

.testimonial-media {
  position: relative;
  width: 210px;
  height: 210px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  // Brand geometry behind the venue logo
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 6px;
    width: 120px;
    height: 120px;
    border-radius: 48% 52% 60% 40% / 55% 45% 55% 45%;
    background: rgba(225, 78, 202, 0.25);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 4px;
    right: 0;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(101, 81, 145, 0.4);
  }

  img {
    position: relative;
    z-index: 1;
    width: 170px;
    height: 170px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.12);
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
  }
}

.testimonial-eyebrow {
  color: $teal;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 0.85rem;
}

.testimonial-pull {
  color: white;
  font-size: 1.65rem;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.01em;
  margin: 0 0 1.25rem;
}

.testimonial-attrib {
  margin: 0 0 0.85rem;

  strong {
    display: block;
    color: white;
    font-size: 0.95rem;
    font-weight: 700;
  }

  span {
    color: $muted-2;
    font-size: 0.88rem;
  }
}

.testimonial-link {
  color: $primary;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  &:hover { text-decoration: underline; color: $primary; }
}

.ref-fade-enter-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.ref-fade-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.ref-fade-enter { opacity: 0; transform: translateY(8px); }
.ref-fade-leave-to { opacity: 0; transform: translateY(-5px); }

/* Selector strip: every venue visible, active one carries the progress bar */

.testimonial-selector {
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  max-width: 1040px;
  margin: 0 auto;
  flex-wrap: wrap;
}

.selector-item {
  flex: 1 1 0;
  min-width: 120px;
  max-width: 190px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  .selector-track {
    display: block;
    height: 2px;
    background: rgba(255, 255, 255, 0.12);
    margin-bottom: 0.85rem;
    overflow: hidden;
  }

  .selector-fill {
    display: block;
    height: 100%;
    width: 100%;
    background: $primary;

    &.animating {
      width: 0;
      animation: ref-progress 8s linear forwards;
    }
  }

  .selector-body {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0 0.5rem 0.5rem;
    opacity: 0.45;
    filter: grayscale(1);
    transition: opacity 0.2s ease, filter 0.2s ease;
  }

  img {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  .selector-name {
    color: $muted;
    font-size: 0.78rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover .selector-body,
  &.active .selector-body {
    opacity: 1;
    filter: none;
  }

  &.active .selector-name { color: white; }
}

@keyframes ref-progress {
  from { width: 0; }
  to { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .selector-fill.animating { animation: none; width: 100%; }
}

@media (max-width: 767px) {
  .testimonial-media { width: 160px; height: 160px; }
  .testimonial-media img { width: 130px; height: 130px; }
  .testimonial-pull { font-size: 1.25rem; }
  .selector-name { display: none; }
  .selector-item { min-width: 48px; }
}

/* Pricing teaser */

.pricing-teaser { padding: 3.5rem 0; }

.pricing-teaser-card {
  background: linear-gradient(135deg, rgba(225, 78, 202, 0.1), rgba(102, 81, 145, 0.08));
  border: 1px solid $primary-border;
  border-radius: 14px;
  padding: 2.5rem;
}

.pricing-teaser-title {
  color: white;
  font-size: 1.65rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.pricing-teaser-sub {
  color: $muted;
  margin: 0;
  line-height: 1.55;
}

/* Demo */

.demo-section {
  padding: 5rem 0;
  background:
    radial-gradient(circle at 0% 50%, rgba(225, 78, 202, 0.08), transparent 50%),
    radial-gradient(circle at 100% 50%, rgba(102, 81, 145, 0.08), transparent 50%);
}

.demo-info { margin-top: 2rem; }
.demo-info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: $muted;
  svg { color: $primary; flex-shrink: 0; }
  a { color: $primary; text-decoration: none; }
  a:hover { text-decoration: underline; }
}

.demo-form {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 2rem;
  h3 { color: white; font-size: 1.25rem; font-weight: 600; margin-bottom: 1.25rem; }
}

.form-label {
  display: block;
  color: $muted;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 0.4rem;
}

/* Mobile tuning */

@media (max-width: 991px) {
  // Side shots crowd the frame at tablet widths; the dashboard carries it
  .hero-tablet,
  .hero-phone { display: none; }
  .hero-visual { padding: 0; }
}

@media (max-width: 768px) {
  .hero { padding: 2.5rem 0 2rem; min-height: 0; }
  .feature-tab-panel { padding: 1.5rem; }
  .pricing-teaser-card { padding: 1.75rem; }
  .pricing-teaser-title { font-size: 1.3rem; }
  .hero-image-col { margin-top: 2rem; }
  .hero-proof span { font-size: 0.78rem; }
}

@media (max-width: 480px) {
  // Below 480px a product screenshot adds no info at thumbnail size
  .hero-image-col { display: none; }
}
</style>
